import React, { useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { useTenantConfiguration } from "../../hooks/useTenantConfiguration";
import { PencilIcon, CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import Loader from "../common/Loader";
import TenantLiveWidgets from "../forms/TenantLiveWidgets";
import { TenantLiveWidgets as TenantLiveWidgetsValue } from "../../schemas/tenantConfigurationSchema";

const TenantConfiguration: React.FC = () => {
  const { profile } = useAuth();
  const tenantId = profile?.tenantId;

  const { configuration, isLoading, updateConfiguration, isUpdating, refetch } =
    useTenantConfiguration(tenantId);

  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>("");

  const handleEditClick = (field: string, currentValue: number | null) => {
    setEditingField(field);
    setEditValue(currentValue?.toString() || "");
  };

  const handleCancel = () => {
    setEditingField(null);
    setEditValue("");
  };

  const handleSaveLiveWidgets = async (liveWidgets: TenantLiveWidgetsValue) => {
    if (!tenantId) {
      alert("Tenant ID not found");
      return;
    }
    try {
      await updateConfiguration({ tenantId, configuration: { liveWidgets } });
      await refetch();
    } catch (error) {
      // Error is handled by toast notification
    }
  };

  const handleSave = async (field: string) => {
    if (!tenantId) {
      alert("Tenant ID not found");
      return;
    }

    const numValue = editValue ? parseInt(editValue, 10) : null;
    if (editValue && isNaN(numValue!)) {
      alert("Please enter a valid number");
      return;
    }

    try {
      const updateData: Record<string, number | null> = {};
      updateData[field] = numValue;

      // Always use update - backend will create configuration if it doesn't exist
      await updateConfiguration({
        tenantId,
        configuration: updateData,
      });

      setEditingField(null);
      setEditValue("");
      await refetch();
    } catch (error) {
      // Error is handled by toast notification
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader />
      </div>
    );
  }

  if (!tenantId) {
    return (
      <div className="text-center py-8 text-gray-500">
        No tenant ID found. Please contact your administrator.
      </div>
    );
  }

  const configFields = [
    {
      key: "tenantPlaylistCreativesPerSlot",
      label: "My Playlist Creatives Per Slot",
      value: configuration?.tenantPlaylistCreativesPerSlot ?? null,
    },
    {
      key: "clientPlaylistCreativesPerSlot",
      label: "Client Playlist Creatives Per Slot",
      value: configuration?.clientPlaylistCreativesPerSlot ?? null,
    },
    {
      key: "clientPlaylistCreativeLimit",
      label: "Client Playlist Creative Limit",
      value: configuration?.clientPlaylistCreativeLimit ?? null,
    },
    {
      key: "clientCreativePlayDuration",
      label: "Client Creative Play Duration (in Seconds)",
      value: configuration?.clientCreativePlayDuration ?? null,
    },
    // {
    //   key: "tenantCreativePlayDuration",
    //   label: "My Creative Play Duration (in Seconds)",
    //   value: configuration?.tenantCreativePlayDuration ?? null,
    // },
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="text-2xl font-semibold text-gray-800">Configuration</h2>
      </div>

      {!configuration && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            No configuration found. Configuration will be created automatically
            when you update any value.
          </p>
        </div>
      )}

      <div className="space-y-4">
        {configFields.map((field) => (
          <div
            key={field.key}
            className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg shadow-sm"
          >
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-700">
                {field.label}
              </label>
              {editingField === field.key ? (
                <div className="flex items-center gap-2 mt-2">
                  <input
                    type="number"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-default"
                    placeholder="Enter value"
                    autoFocus
                  />
                  <button
                    onClick={() => handleSave(field.key)}
                    disabled={isUpdating}
                    className="p-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors disabled:opacity-50"
                    title="Save"
                  >
                    <CheckIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={isUpdating}
                    className="p-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors disabled:opacity-50"
                    title="Cancel"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              ) : (
                <p className="mt-1 text-base text-gray-900">
                  {field.value !== null ? field.value : "Not set"}
                </p>
              )}
            </div>
            {editingField !== field.key && (
              <button
                onClick={() => handleEditClick(field.key, field.value)}
                className="ml-4 p-2 text-gray-500 hover:text-primary-default hover:bg-gray-100 rounded transition-colors"
                title="Edit"
              >
                <PencilIcon className="h-5 w-5" />
              </button>
            )}
          </div>
        ))}
      </div>

      <TenantLiveWidgets
        value={configuration?.liveWidgets}
        onSave={handleSaveLiveWidgets}
        isSaving={isUpdating}
      />
    </div>
  );
};

export default TenantConfiguration;
